import { Box, Stack, Typography } from "@mui/material";
import { useCallback } from "react";
import { PatientWeight } from "src/api/types";
import WhiteCard from "src/shared/components/WhiteCard";
import MedicationZone from "./components/MedicationZone";
import ProviderOutlinedIcon from "src/assets/icons/ProviderOutlinedIcon";
import { useTranslation } from "react-i18next";

interface CardProps {
  patientWeight: PatientWeight;
  page: string;
  onNext: () => void;
}

const BmiCard = ({ patientWeight, page, onNext }: CardProps) => {
  const component = "bmiCard";
  const { t } = useTranslation();
  const inchesToFeet = useCallback((totalInches: number) => {
    const feet = Math.floor(totalInches / 12);
    const inches = String(totalInches % 12);

    return `${feet}'${inches}"`;
  }, []);

  const indicatorLabel = t(`${page}.${component}.currentBmi`);
  const zoneLabel = t(`${page}.${component}.medicationZone`);
  const threshold = [10, 24, 40, 50];
  const zoneRange = [24, 50];

  return (
    <WhiteCard
      spacing={4}
      height="max-content"
      sx={{
        borderRadius: "30px",
        background: `
      linear-gradient(
        180deg,
        rgba(255, 255, 255, 0) 0.31%,
        rgba(255, 255, 255, 0.36) 99.69%
      ),
      #00B6B9
    `,
      }}
      onClick={onNext}
    >
      <Stack direction="row" alignItems="end" justifyContent="space-around">
        <Stack gap={1}>
          <Typography color="#ffffff">
            {t(`${page}.${component}.bmi`)}
          </Typography>
          <Typography color="#ffffff" variant="h3" fontWeight={600}>
            {patientWeight?.bmi}
          </Typography>
        </Stack>
        <Stack gap={1}>
          <Typography color="#ffffff">
            {t(`${page}.${component}.weight`)}
          </Typography>
          <Typography color="#ffffff" variant="h4" fontWeight={600}>
            {patientWeight.weight} lbs
          </Typography>
        </Stack>
        <Stack gap={1}>
          <Typography color="#ffffff">
            {t(`${page}.${component}.height`)}
          </Typography>
          <Typography color="#ffffff" variant="h4" fontWeight={600}>
            {inchesToFeet(patientWeight.height)}
          </Typography>
        </Stack>
      </Stack>
      <Stack gap={4}>
        <MedicationZone
          value={patientWeight.bmi}
          indicatorLabel={indicatorLabel}
          zoneLabel={zoneLabel}
          threshold={threshold}
          zoneRange={zoneRange}
        />
        <Stack gap={2}>
          <Box alignSelf="center">
            <ProviderOutlinedIcon />
          </Box>
          <Typography color="#ffffff" textAlign="center" variant="h6">
            {t(`${page}.${component}.subtext`)}
          </Typography>
        </Stack>
      </Stack>
    </WhiteCard>
  );
};

export default BmiCard;
